class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.localizedProductCache = {};
    this.localizedProductsArIndex = null;
    this.latestQueryKey = null;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.debounceTimer = null;
    this.searchTerm = '';
    this.currentQueryLocale = this.getLocaleForQuery('');
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  debounce(func, delay) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(func, delay);
  }

  getLocaleForQuery(term) {
    const value = (term || '').trim();
    const htmlLang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const safeFallback = htmlLang === 'ar' ? 'en' : htmlLang;
    if (!value) return safeFallback;
    if (/[\u0600-\u06FF]/.test(value)) return 'ar';
    if (/[A-Za-z]/.test(value)) return 'en';
    return safeFallback;
  }

  getProductHandleFromUrl(href) {
    if (!href) return null;
    const match = href.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  getLocalizedProductsArIndex() {
    if (this.localizedProductsArIndex) return this.localizedProductsArIndex;
    const localizedJson = document.getElementById('localized-products-ar');
    if (!localizedJson) return null;
    try {
      const products = JSON.parse(localizedJson.textContent || '[]');
      const index = {};
      products.forEach((product) => {
        if (product && product.handle) index[product.handle] = product;
      });
      this.localizedProductsArIndex = index;
      return index;
    } catch (e) {
      return null;
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  async fetchLocalizedProductTitle(handle, uiLocale) {
    if (!handle) return null;
    const cacheKey = `${uiLocale}-${handle}`;
    if (this.localizedProductCache[cacheKey]) return this.localizedProductCache[cacheKey];

    // Prefer the embedded Arabic index when available (faster + fewer requests).
    if (uiLocale === 'ar') {
      const arIndex = this.getLocalizedProductsArIndex();
      const fromIndex = arIndex && arIndex[handle] && arIndex[handle].title;
      if (fromIndex) {
        const data = { title: fromIndex };
        this.localizedProductCache[cacheKey] = data;
        return data;
      }
    }

    const prefix = uiLocale === 'ar' ? '' : ('/' + uiLocale);
    try {
      const response = await fetch(`${prefix}/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const data = await response.json();
      if (!data || !data.title) return null;
      const result = { title: data.title };
      this.localizedProductCache[cacheKey] = result;
      return result;
    } catch (e) {
      return null;
    }
  }

  async localizePredictiveSearchResultsMarkup(resultsMarkup, uiLocale, queryKey, searchTerm) {
    if (uiLocale !== 'ar') return resultsMarkup;
    if (!resultsMarkup) return resultsMarkup;

    const container = document.createElement('div');
    container.innerHTML = resultsMarkup;

    const noResultsBlock = container.querySelector('.message-search-found');
    if (noResultsBlock) {
      const paragraphs = Array.from(noResultsBlock.querySelectorAll('p'));
      if (paragraphs[0]) paragraphs[0].textContent = 'لم يتم العثور على منتجات مطابقة لبحثك.';
      if (paragraphs[1]) {
        paragraphs[1].innerHTML = `نتائج البحث عن "<b>${this.escapeHtml(searchTerm)}</b>": 0 منتج.`;
      }
      return container.innerHTML;
    }

    const productLinks = Array.from(container.querySelectorAll('a.predictive-search__item--link-with-thumbnail'));
    const handles = productLinks
      .map((link) => this.getProductHandleFromUrl(link.getAttribute('href')))
      .filter(Boolean);

    const uniqueHandles = Array.from(new Set(handles));
    if (uniqueHandles.length === 0) return resultsMarkup;

    const localizedByHandle = {};
    await Promise.all(
      uniqueHandles.map(async (handle) => {
        // Eğer kullanıcı yeni bir query'e geçtiyse eski sonuçları işlemeyi bırak.
        if (this.latestQueryKey && this.latestQueryKey !== queryKey) return;
        const localized = await this.fetchLocalizedProductTitle(handle, uiLocale);
        if (localized && localized.title) localizedByHandle[handle] = localized.title;
      })
    );

    productLinks.forEach((link) => {
      const handle = this.getProductHandleFromUrl(link.getAttribute('href'));
      const localizedTitle = handle && localizedByHandle[handle];
      if (!localizedTitle) return;

      const titleEl = link.querySelector('.predictive-search__item-heading');
      if (titleEl) titleEl.textContent = localizedTitle;

      const img = link.querySelector('img');
      if (img) img.setAttribute('alt', localizedTitle);
    });

    const searchForText = container.querySelector('[data-predictive-search-search-for-text]');
    if (searchForText) {
      searchForText.textContent = `جميع المنتجات لـ ${searchTerm}`;
    }

    return container.innerHTML;
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;

      // Get the translation key text from the element's original content
      const locale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
      let baseText = '';

      if (locale === 'ar') {
        baseText = 'جميع المنتجات لـ';
      } else {
        baseText = 'All products for';
      }

      const newButtonText = `${baseText} ${newTerm}`;
      searchForTextElement.innerText = newButtonText;
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!newSearchTerm.length) {
      this.searchTerm = '';
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const style = document.getElementById("predictive-search-style");
      if (style) style.remove();
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.querySelector('#predictive-search-option-search-keywords')?.remove();
      this.loadCollectionProducts("search-collections");
      this.open();
      return;
    }

    if (!this.searchTerm || newSearchTerm !== this.searchTerm) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.getSearchResults(newSearchTerm);
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!document.getElementById("predictive-search-style")) {
      const newStyle = document.createElement("style");
      newStyle.id = "predictive-search-style";
      newStyle.textContent = `
        @media (min-width:1024px) {
          #predictive-search-results-groups-wrapper { display: grid !important; grid-template-columns: 100% !important; }
          .predictive-search__results-list { grid-template-columns: 1fr 1fr 1fr 1fr 1fr }
        }
      `;
      document.head.appendChild(newStyle);
    }

    this.debounce(() => {
      this.getSearchResults(this.searchTerm);
    }, 300);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Ipekyol SKU'ları "IS" + rakamlarla başlıyor. Shopify'ın search motoru EN dilinde
    // kısmi/prefix SKU eşleştirmesi yapmıyor (Shopify destek de doğruladı). Input'un
    // görünen değerine dokunmadan, sorguyu yıldızlı haliyle manuel yönlendiriyoruz.
    const term = this.getQuery();
    if (/^is\d[a-z0-9]*$/i.test(term)) {
      event.preventDefault();
      const searchUrl = (window.routes && window.routes.search_url) || '/search';
      window.location.href = `${searchUrl}?q=${encodeURIComponent(term + '*')}&options%5Bprefix%5D=last`;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.open();
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.loadCollectionProducts("search-collections");
      this.open();
      return;
    }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Sadece search gerçekten açıksa kapat
        // if (this.getAttribute('open') === 'true') {
        //   this.close();
        // }
      }
    }, 200); // Timeout'u artırarak input focus olma süresini ver
  }

  onKeyup(event) {
    const isEmpty = !this.getQuery().length;
    if (isEmpty) {
      // AR için stili hemen kaldır (loadCollectionProducts senkron çalıştığından
      // stil aktifken render edilirse menü yanlış konuma düşer).
      // EN için stili burada kaldırmaya gerek yok: loadCollectionProducts async
      // (fetch) çalışır; eski arama sonuçları wrapper'ı hâlâ DOM'da olduğundan
      // stil erkenden kaldırılırsa içerik küçülme efekti oluşur.
      // Stil + wrapper temizliği EN'de onChange tarafından atomik yapılır.
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      const locale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
      if (locale === 'ar') {
        const style = document.getElementById("predictive-search-style");
        if (style) style.remove();
      }
      this.querySelector('#predictive-search-option-search-keywords')?.remove();
      this.loadCollectionProducts("search-collections");
      this.open();
    }

    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  getSearchResults(searchTerm) {
    const uiLocale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const queryLocale = this.getLocaleForQuery(searchTerm);
    this.currentQueryLocale = queryLocale;
    const queryKey = `${uiLocale}-${queryLocale}-${searchTerm.replace(' ', '-').toLowerCase()}`;
    this.latestQueryKey = queryKey;
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], uiLocale);
      return;
    }

    const predictiveSearchUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    // Prefix'siz (varsayılan dil) /search/suggest isteği bu mağazada "417 Unsupported buyer locale"
    // hatasıyla tamamen başarısız oluyor. O durumda çalışan /search (tam metin arama) endpoint'ine
    // düşüyoruz. Diğer diller için /search/suggest'i, SKU/barkod alanını da arama kapsamına dahil
    // eden resources[options][fields] parametresiyle kullanıyoruz (ipekyol-uae temasında doğrulanmış).
    const isUnprefixedSuggestUrl = predictiveSearchUrl === '/search/suggest';
    const fetchUrl = isUnprefixedSuggestUrl
      ? `/search?q=${encodeURIComponent(searchTerm)}&type=product&section_id=search-ajax-results`
      : `${predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,tag,variants.sku`;
    const sectionSelector = isUnprefixedSuggestUrl
      ? '#shopify-section-search-ajax-results'
      : '#shopify-section-predictive-search';

    fetch(fetchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then(async (text) => {
        if (this.latestQueryKey && this.latestQueryKey !== queryKey) return;

        const doc = new DOMParser().parseFromString(text, 'text/html');
        const searchSection = doc.querySelector(sectionSelector);
        if (!searchSection) {
          this.removeAttribute('loading');
          return;
        }
        const resultsMarkup = searchSection.innerHTML;
        if (this.latestQueryKey && this.latestQueryKey !== queryKey) return;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, uiLocale);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.removeAttribute('loading');
        this.open();
        console.error("Search fetch error:", error);
      });
  }

  getArabicSearchResults(searchTerm, queryKey) {
    const localizedJson = document.getElementById('localized-products-ar');
    if (!localizedJson) return;

    try {
      const allProducts = JSON.parse(localizedJson.textContent);

      // Filter products based on search term (search in title and vendor)
      const searchLower = searchTerm.toLowerCase();
      const filteredProducts = allProducts.filter(product =>
        product.title.toLowerCase().includes(searchLower) ||
        product.vendor.toLowerCase().includes(searchLower)
      );

      this._renderArabicResults(filteredProducts, searchTerm, queryKey);
    } catch (e) {
      console.error("Error parsing Arabic products JSON:", e);
      this.close();
    }
  }

  _renderArabicResults(filteredProducts, searchTerm, queryKey) {
    try {

      if (filteredProducts.length === 0) {
        const noResultsMarkup = `
          <div id="predictive-search-results">
            <div class="message-search-found">
              <style>
                .message-search-found {
                  margin-bottom: 80px;
                  margin-top: 80px;
                }
                .message-ico {
                  background: #f8f8f8;
                  width: 110px;
                  height: 110px;
                  border-radius: 100%;
                  text-align: center;
                  justify-content: center;
                  align-items: center;
                  margin: 0 auto 20px auto;
                  display: flex;
                }
                .message-search-found svg {
                  font-size: 40px;
                  color: #000;
                  animation: jackInTheBox 1s .5s;
                  transform: scale(0.85);
                  animation-fill-mode: both;
                }
                .message-search-found svg.icon {
                  width: 40px;
                  height: 40px;
                  stroke-width: 0;
                  stroke: currentColor;
                  fill: currentColor;
                  vertical-align: middle;
                }
                .message-search-found p {
                  animation: text 1s .5s;
                  animation-fill-mode: both;
                  text-align: center!important;
                  line-height: 18px;
                }
              </style>
              <div class="message-ico">
                <svg class="icon icon-search" aria-hidden="true" focusable="false">
                  <use href="#icon-search"></use>
                </svg>
              </div>
              <p>
                لم يتم العثور على منتجات مطابقة لبحثك.
              </p>
              <p>
                نتائج البحث عن "<b>${searchTerm}</b>": 0 منتج.
              </p>
            </div>
          </div>
        `;
        this.renderSearchResults(noResultsMarkup, this.currentQueryLocale);
        return;
      }

      // Build the products markup
      const productsMarkup = filteredProducts.map((product, index) => {
        const productImage = product.image || '';
        const _pLang = document.documentElement.lang || 'ar';
        const urlPrefix = _pLang === 'ar' ? '' : ('/' + _pLang);

        const price = this.toNumber(product.price);
        const compareAt = this.toNumber(product.compare_at_price);
        const isDiscount = compareAt > price;

        return `
          <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${urlPrefix}/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
              <img class="predictive-search__image" src="${productImage}" alt="${product.title}" width="auto">
              <div class="predictive-search__item-content">
                <span class="visually-hidden">Vendor:</span>
                <div class="predictive-search__item-vendor caption-with-letter-spacing">
                  ${product.vendor}
                </div>
                <p class="predictive-search__item-heading h5">
                  ${product.title}
                </p>
                <div class="text-center-mobile price">
                  <div class="price__container">
                    ${isDiscount ? `
                      <div class="price__sale" style="display:unset!important">
                        <span>
                          <s class="price-item price-item--regular">
                            ${product.compare_at_price}
                          </s>
                        </span>
                        <span class="price-item price-item--sale price-item--last">
                          ${product.price}
                        </span>
                      </div>
                    ` : `
                      <div class="price__regular">
                        <span class="price-item price-item--regular">
                          ${product.price}
                        </span>
                      </div>
                    `}
                  </div>
                </div>
              </div>
            </a>
          </li>
        `;
      }).join('');

      const resultsMarkup = `
        <div id="predictive-search-results" role="listbox">
          <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
            <div class="predictive-search__result-group"></div>
            <div class="predictive-search__result-group">
              <div>
                <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
                  المنتجات
                </h2>
                <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                  ${productsMarkup}
                </ul>
              </div>
            </div>
          </div>
          <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
            <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
              <style>
                .predictive-search__item--term{
                  min-width: 280px;
                  text-align: center;
                  background: none;
                  width: auto;
                  border: 1px solid #000;
                  padding: 14px 0;
                  margin: 35px 20px;
                  height: 46px;
                  display: flex;
                  justify-content: center;
                  color: #000;
                  font-family: 'Work Sans';
                  font-weight: 400;
                }
                .predictive-search__item--term span{
                  font-family: 'Work Sans' !important;
                }
              </style>
              <span data-predictive-search-search-for-text>
                جميع المنتجات لـ ${searchTerm}
              </span>
            </button>
          </div>
        </div>
      `;

      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      this.renderSearchResults(resultsMarkup, this.currentQueryLocale);
    } catch (e) {
      console.error("Error parsing Arabic products JSON:", e);
      this.close();
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  // Yardımcı: sayıyı güvenle parse et
  toNumber(v) {
    return Number(String(v ?? 0).replace(/[^\d.]/g, '')); // virgül/boşluk/simge temizler
  }

  loadCollectionProducts(collectionHandle) {
    // Empty keyword state should follow storefront UI locale.
    const locale = (document.documentElement.lang || this.currentQueryLocale || 'en').toLowerCase().split('-')[0];
    this.currentQueryLocale = locale;
    if (locale === 'ar') {
      const localizedJson = document.getElementById('localized-products-ar');
      if (localizedJson) {
        try {
          const products = JSON.parse(localizedJson.textContent);
          const displayProducts = products.slice(0, 12);

          const productsMarkup = displayProducts.map((product, index) => {
            const productImage = product.image || '';
            let urlPrefix = "";
            if (locale !== 'ar') urlPrefix = '/' + locale;

            const price = this.toNumber(product.price);
            const compareAt = this.toNumber(product.compare_at_price);
            const isDiscount = compareAt > price;

            return `
              <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
                <a href="${urlPrefix}/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                  <img class="predictive-search__image" src="${productImage}" alt="${product.title}" width="auto">
                  <div class="predictive-search__item-content">
                    <span class="visually-hidden">Vendor:</span>
                    <div class="predictive-search__item-vendor caption-with-letter-spacing">
                      ${product.vendor}
                    </div>
                    <p class="predictive-search__item-heading h5">
                      ${product.title}
                    </p>
                    <div class="text-center-mobile price">
                      <div class="price__container">
                        ${isDiscount ? `
                          <div class="price__sale" style="display:unset!important">
                            <span>
                              <s class="price-item price-item--regular">
                                ${product.compare_at_price}
                              </s>
                            </span>
                            <span class="price-item price-item--sale price-item--last">
                              ${product.price}
                            </span>
                          </div>
                        ` : `
                          <div class="price__regular">
                            <span class="price-item price-item--regular">
                              ${product.price}
                            </span>
                          </div>
                        `}
                      </div>
                    </div>
                  </div>
                </a>
              </li>
            `;
          });

          const halfLength = Math.ceil(products.length / 2);
          const firstHalfProducts = productsMarkup.slice(0, halfLength);
          const secondHalfProducts = productsMarkup.slice(halfLength);

          const menuDataTitle = document.getElementById('predictive-search-menu').getAttribute('data-title');
          const menuData = JSON.parse(document.getElementById('predictive-search-menu').getAttribute('data-menu'));
          const menuHtml = menuData.map(item => `<a href="${item.url}">${item.title}</a>`).join('');

          const isMobile = window.matchMedia('(max-width: 768px)').matches;

          if (isMobile) {
            this.renderSearchResults(`
              <div id="predictive-search-results" role="listbox">
                <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
                  <div class="predictive-search__result-group"></div>
                  <div class="predictive-search__result-group morder2">
                    <ul id="predictive-search-results-products-list-first" class="predictive-search__results-list predictive-search-results-products-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                      ${firstHalfProducts.join('')}
                    </ul>
                    <ul id="predictive-search-results-products-list-second" class="predictive-search__results-list predictive-search-results-products-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                      ${secondHalfProducts.join('')}
                    </ul>
                  </div>
                  <div class="predictive-search__result-group morder1">
                    <h6 class="predictive-search-menu-title">${menuDataTitle}</h6>
                    <div class="predictive-search-menu">${menuHtml}</div>
                  </div>
                </div>
              </div>
            `, locale);
          } else {
            this.renderSearchResults(`
              <div id="predictive-search-results" role="listbox">
                <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
                  <div class="predictive-search__result-group"></div>
                  <div class="predictive-search__result-group">
                    <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                      ${productsMarkup.join('')}
                    </ul>
                  </div>
                  <div class="predictive-search__result-group morder1">
                    <h6 class="predictive-search-menu-title">${menuDataTitle}</h6>
                    <div class="predictive-search-menu">${menuHtml}</div>
                  </div>
                </div>
              </div>
            `, locale);
          }
        } catch (e) {
          this.close();
        }
        return;
      }
    }

    const _collLocalePrefix = locale === 'ar' ? '' : ('/' + locale);
    fetch(`${_collLocalePrefix}/collections/${collectionHandle}/products.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Hatası: ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (!data.products || data.products.length === 0) return;

        const limitedProducts = data.products.slice(0, 12);
        const _cLang = document.documentElement.lang || 'ar';
        const _cPrefix = _cLang === 'ar' ? '' : ('/' + _cLang);
        const productsMarkup = limitedProducts.map((product, index) => {
          const productImage = product.images && product.images.length > 0 ? product.images[0].src : '';

          const variant = product.variants?.[0] || {};
          const price = this.toNumber(variant.price);
          const compareAt = this.toNumber(variant.compare_at_price);
          const isDiscount = compareAt > price;

          return `
            <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${_cPrefix}/products/${product.handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                <img class="predictive-search__image" src="${productImage}" alt="${product.title}" width="auto">
                <div class="predictive-search__item-content">
                  <span class="visually-hidden">Vendor:</span>
                  <div class="predictive-search__item-vendor caption-with-letter-spacing">
                    ${product.vendor}
                  </div>
                  <p class="predictive-search__item-heading h5">
                    ${product.title}
                  </p>
                  <div class="text-center-mobile price">
                    <div class="price__container">
                      ${isDiscount
                        ? `
                          <div class="price__sale" style="display:unset!important">
                            <span>
                              <s class="price-item price-item--regular">
                                ${variant.compare_at_price} ${Shopify.currency.active}
                              </s>
                            </span>
                            <span class="price-item price-item--sale price-item--last">
                              ${variant.price} ${Shopify.currency.active}
                            </span>
                          </div>
                        `
                        : `
                          <div class="price__regular">
                            <span class="price-item price-item--regular">
                              ${variant.price} ${Shopify.currency.active}
                            </span>
                          </div>
                        `
                      }
                    </div>
                  </div>
                </div>
              </a>
            </li>
          `;
        });

        const halfLength = Math.ceil(limitedProducts.length / 2);
        const firstHalfProducts = productsMarkup.slice(0, halfLength);
        const secondHalfProducts = productsMarkup.slice(halfLength);

        const menuDataTitle = document.getElementById('predictive-search-menu').getAttribute('data-title');
        const menuData = JSON.parse(document.getElementById('predictive-search-menu').getAttribute('data-menu'));
        const menuHtml = menuData.map(item => `<a href="${item.url}">${item.title}</a>`).join('');

        const isMobile = window.matchMedia('(max-width: 768px)').matches;

        if (isMobile) {
          this.renderSearchResults(`
            <div id="predictive-search-results" role="listbox">
              <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
                <div class="predictive-search__result-group"></div>
                <div class="predictive-search__result-group morder2">
                  <ul id="predictive-search-results-products-list-first" class="predictive-search__results-list predictive-search-results-products-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                    ${firstHalfProducts.join('')}
                  </ul>
                  <ul id="predictive-search-results-products-list-second" class="predictive-search__results-list predictive-search-results-products-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                    ${secondHalfProducts.join('')}
                  </ul>
                </div>
                <div class="predictive-search__result-group morder1">
                  <h6 class="predictive-search-menu-title">${menuDataTitle}</h6>
                  <div class="predictive-search-menu">${menuHtml}</div>
                </div>
              </div>
            </div>
          `, locale);
        } else {
          this.renderSearchResults(`
            <div id="predictive-search-results" role="listbox">
              <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
                <div class="predictive-search__result-group"></div>
                <div class="predictive-search__result-group">
                  <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                    ${productsMarkup.join('')}
                  </ul>
                </div>
                <div class="predictive-search__result-group morder1">
                  <h6 class="predictive-search-menu-title">${menuDataTitle}</h6>
                  <div class="predictive-search-menu">${menuHtml}</div>
                </div>
              </div>
            </div>
          `, locale);
        }

      })
      .catch(error => {
        console.error("Error loading collection products:", error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      this.statusElement = document.createElement('div');
      this.statusElement.classList.add('predictive-search-status');
      this.appendChild(this.statusElement);
    }
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);

    const wrapper = document.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) return;
    const groups = wrapper.querySelectorAll('.predictive-search__result-group');
    if (groups.length === 1) {
      groups[0].style.setProperty('display', 'unset', 'important');
    } else {
      groups.forEach(group => group.style.removeProperty('display'));
    }
  }

  renderSearchResults(resultsMarkup, localeOverride) {
    const locale = (localeOverride || this.currentQueryLocale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    this.currentQueryLocale = locale;
    if (locale !== 'ar') {
      resultsMarkup = resultsMarkup.replace(/href="\/products\//g, `href="/${locale}/products/`);
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.updateSearchForTerm(null, this.getQuery());
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.removeAttribute('loading');
    this.removeAttribute('open');
  }
}

customElements.define('predictive-search', PredictiveSearch);